import { useEffect, useState } from "react";
import { Analytics } from "@vercel/analytics/react";
import Button from "./ui/Button";

// Stored in localStorage so the choice persists across visits. We only ever
// load analytics (which sets cookies / fingerprints) AFTER an explicit accept.
const CONSENT_KEY = "vein-cookie-consent";
type Consent = "accepted" | "rejected";

const readConsent = (): Consent | null => {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(CONSENT_KEY);
  return v === "accepted" || v === "rejected" ? v : null;
};

/**
 * Renders the cookie consent banner on first visit and gates Vercel Analytics
 * behind explicit consent. Self-contained: drop-in replacement for a bare
 * <Analytics /> at the app root. CCPA / general-best-practice compliant —
 * no non-essential cookies are set until the user accepts.
 */
const CookieConsent: React.FC = () => {
  const [consent, setConsent] = useState<Consent | null>(null);
  // Avoid a flash of the banner before we've read localStorage on the client.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setConsent(readConsent());
    setHydrated(true);
  }, []);

  const choose = (value: Consent) => {
    window.localStorage.setItem(CONSENT_KEY, value);
    setConsent(value);
  };

  const showBanner = hydrated && consent === null;

  return (
    <>
      {/* Analytics only loads once consent is explicitly granted */}
      {consent === "accepted" && <Analytics />}

      {showBanner && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-[60] sm:max-w-md"
        >
          <div className="bg-white border border-vein-border rounded-xl p-5">
            <h2
              id="cookie-consent-title"
              className="text-sm font-bold text-gray-900 mb-1"
            >
              We value your privacy
            </h2>
            <p
              id="cookie-consent-desc"
              className="text-xs text-gray-600 leading-relaxed"
            >
              We use a small number of analytics cookies to understand how the
              app is used and improve it. No analytics cookies are set unless you
              accept. See our{" "}
              <a
                href="/privacy"
                className="text-vein-red-600 font-medium underline hover:text-vein-red-700"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://vein.finance/cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="text-vein-red-600 font-medium underline hover:text-vein-red-700"
              >
                Cookie Policy
              </a>
              .
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                variant="secondary"
                type="button"
                onClick={() => choose("rejected")}
                className="flex-1"
              >
                Decline
              </Button>
              <Button
                type="button"
                onClick={() => choose("accepted")}
                className="flex-1"
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
